import { Badge, Box, Button, Flex, Grid, Spinner, Text, useToast } from "@chakra-ui/react";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { useEffect, useRef, useState } from "react";
import { FaFileDownload } from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import Layout from "../../../components/Layout";
import {
  DistribuidoraDashboard,
  DistribuidoraInvoicesPage,
  DistribuidoraParams,
  getDistribuidoraDashboard,
  getDistribuidoraInvoices,
} from "../../../services/gestaoCommercialService";
import { crm } from "../../../theme";
import { useCompany } from "../../../contexts/CompanyContext";
import { csvNumber, downloadCsv } from "../comercial/shared/exportCsv";
import {
  EmptyState,
  FieldLabel,
  KpiTile,
  NO_DATA_HINT,
  PageHeader,
  Panel,
  RankBar,
  brl,
  brl0,
  fmtDate,
  monthLabel,
  num,
  pctFmt,
  selectStyle,
  tableCellStyle,
  tableHeadStyle,
} from "../comercial/shared/ui";

const PAGE_SIZE = 25;
const PIE_COLORS = [crm.brand, "#0891b2", "#7c3aed", "#ea580c"];
const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

type SortKey = "issueDate" | "number" | "client" | "process" | "products" | "taxes" | "total" | "status";

const columns: { key: SortKey; label: string; right?: boolean }[] = [
  { key: "issueDate", label: "Data / empresa" },
  { key: "number", label: "NF" },
  { key: "client", label: "Cliente" },
  { key: "process", label: "Processo / declaração" },
  { key: "products", label: "Produtos", right: true },
  { key: "taxes", label: "Tributos", right: true },
  { key: "total", label: "Total", right: true },
  { key: "status", label: "Situação" },
];

const Distribuidora = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const toast = useToast();
  const { currentEmulation } = useCompany();

  const [companyType, setCompanyType] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState("");
  // Filtro por nome vale só para o cliente emulado em que foi escolhido: trocar a emulação
  // o zera já no mesmo render (sem requisição extra com o filtro antigo).
  const [clientFilter, setClientFilter] = useState({ value: "", emulation: currentEmulation });
  const client = clientFilter.emulation === currentEmulation ? clientFilter.value : "";
  const setClient = (value: string) => setClientFilter({ value, emulation: currentEmulation });
  const [status, setStatus] = useState<"all" | "active" | "reversed">("all");

  const [data, setData] = useState<DistribuidoraDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [table, setTable] = useState<DistribuidoraInvoicesPage | null>(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortKey>("issueDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const seq = useRef(0);
  const tableSeq = useRef(0);

  const filters = (): DistribuidoraParams => ({
    companyType: companyType ? Number(companyType) : undefined,
    year: year ? Number(year) : undefined,
    month: month ? Number(month) : undefined,
    client: client || undefined,
    status,
    companyId: currentEmulation || undefined,
  });

  const filterKey = JSON.stringify([companyType, year, month, client, status, currentEmulation]);
  const tableFilterKey = useRef(filterKey);

  useEffect(() => {
    const id = ++seq.current;
    setLoading(true);
    getDistribuidoraDashboard(filters())
      .then((r) => id === seq.current && setData(r))
      .catch((error) => {
        if (id !== seq.current) return;
        console.log("error", error);
        toast({ title: "Não foi possível carregar o painel da Distribuidora.", status: "error" });
      })
      .finally(() => id === seq.current && setLoading(false));
  }, [filterKey]);

  useEffect(() => {
    // Filtro mudou fora da 1ª página: volta pra página 1 e deixa a próxima rodada buscar (evita buscar 2x).
    // tableFilterKey = último filtro já tratado; só a rodada com page=1 busca.
    if (tableFilterKey.current !== filterKey) {
      tableFilterKey.current = filterKey;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    const id = ++tableSeq.current;
    setTableLoading(true);
    getDistribuidoraInvoices({ ...filters(), page, pageSize: PAGE_SIZE, sortBy, sortDir })
      .then((r) => id === tableSeq.current && setTable(r))
      .catch((error) => {
        if (id !== tableSeq.current) return;
        console.log("error", error);
        toast({ title: "Não foi possível carregar as notas fiscais.", status: "error" });
      })
      .finally(() => id === tableSeq.current && setTableLoading(false));
  }, [filterKey, page, sortBy, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(key);
      setSortDir(key === "issueDate" || key === "products" || key === "taxes" || key === "total" ? "desc" : "asc");
    }
    setPage(1);
  };

  const exportInvoices = async () => {
    try {
      const all = await getDistribuidoraInvoices({ ...filters(), page: 1, pageSize: 5000, sortBy, sortDir });
      downloadCsv(
        "notas-fiscais-distribuidora",
        ["Data emissão", "Empresa", "NF", "Série", "Cliente", "Processo", "Declaração", "Produtos", "Tributos", "Total", "Situação"],
        all.items.map((i) => [
          fmtDate(i.issueDate),
          i.company,
          i.number,
          i.series,
          i.client,
          i.processRef,
          i.declarationNumber,
          csvNumber(i.products),
          csvNumber(i.taxes),
          csvNumber(i.total),
          i.status,
        ])
      );
    } catch (error) {
      console.log("error", error);
      toast({ title: "Não foi possível exportar.", status: "error" });
    }
  };

  const totalPages = table ? Math.max(1, Math.ceil(table.totalCount / table.pageSize)) : 1;
  const maxClient = Math.max(0, ...(data?.topClients ?? []).map((c) => c.total));
  const maxProcess = Math.max(0, ...(data?.topProcesses ?? []).map((c) => c.total));
  const taxRows = data
    ? [
        { label: "PIS", value: data.taxes.pis },
        { label: "COFINS", value: data.taxes.cofins },
        { label: "IPI", value: data.taxes.ipi },
        { label: "ICMS", value: data.taxes.icms },
        { label: "ICMS ST + FCP", value: data.taxes.icmsStFcp },
      ]
    : [];
  const maxTax = Math.max(0, ...taxRows.map((t) => t.value));

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="auto">
      <Layout>
        <Box p="24px" bg={crm.pageBg} minH="100%">
          <PageHeader
            title="Distribuidora"
            subtitle="Notas de saída (NF-e emitidas pelo sistema + planilhas importadas) por empresa, cliente e competência."
          />

          <Flex gap="16px" mb="20px" flexWrap="wrap" align="flex-end">
            <Box>
              <FieldLabel>Empresa</FieldLabel>
              <select style={selectStyle} value={companyType} onChange={(e) => setCompanyType(e.target.value)}>
                <option value="">Todas</option>
                <option value="1">CVF Logística</option>
                <option value="2">Distribuidora</option>
                <option value="3">Filial RO</option>
                <option value="4">WTrading</option>
              </select>
            </Box>
            <Box>
              <FieldLabel>Ano</FieldLabel>
              <select style={selectStyle} value={year} onChange={(e) => setYear(e.target.value)}>
                <option value="">Todos</option>
                {year && !data?.yearOptions.includes(Number(year)) && <option value={year}>{year}</option>}
                {(data?.yearOptions ?? []).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </Box>
            <Box>
              <FieldLabel>Competência</FieldLabel>
              <select style={selectStyle} value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value="">Todos os meses</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </Box>
            <Box>
              <FieldLabel>Cliente</FieldLabel>
              <select style={{ ...selectStyle, maxWidth: "260px" }} value={client} onChange={(e) => setClient(e.target.value)}>
                <option value="">Todos</option>
                {client && !data?.clientOptions.includes(client) && <option value={client}>{client}</option>}
                {(data?.clientOptions ?? []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Box>
            <Box>
              <FieldLabel>Situação</FieldLabel>
              <select style={selectStyle} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                <option value="all">Todas</option>
                <option value="active">Somente ativas</option>
                <option value="reversed">Estornadas / canceladas</option>
              </select>
            </Box>
          </Flex>

          {loading && !data ? (
            <Flex justify="center" py="60px">
              <Spinner color={crm.brand} />
            </Flex>
          ) : !data ? (
            <EmptyState title="Não foi possível carregar os dados." />
          ) : !data.hasData ? (
            <Panel title="Notas fiscais de saída">
              <EmptyState
                title="Nenhuma nota fiscal de saída."
                hint={`NF-e de produção emitidas pelo sistema entram sozinhas. ${NO_DATA_HINT}`}
              />
            </Panel>
          ) : (
            <Box opacity={loading ? 0.6 : 1} transition="opacity .15s">
              <Grid templateColumns={isMobile ? "1fr 1fr" : "repeat(5, 1fr)"} gap="16px" mb="8px">
                <KpiTile
                  title="Faturamento total"
                  value={brl0.format(data.kpis.total)}
                  hint={`${num.format(data.kpis.invoices)} NFs${status === "all" ? " ativas" : ""}`}
                />
                <KpiTile title="Valor dos produtos" value={brl0.format(data.kpis.products)} />
                <KpiTile title="Processos identificados" value={num.format(data.kpis.processes)} />
                <KpiTile
                  title="Clientes ativos"
                  value={num.format(data.kpis.activeClients)}
                  hint={`Ticket médio por NF ${brl0.format(data.kpis.averageTicket)}`}
                />
                <KpiTile
                  title="Tributos destacados"
                  value={brl0.format(data.kpis.taxes)}
                  hint={`${pctFmt(data.kpis.taxesPercent)} dos produtos`}
                />
              </Grid>
              {status === "all" && data.kpis.reversedInvoices > 0 && (
                <Text fontSize="xs" color={crm.textMuted} mb="16px">
                  Totais consideram só NFs ativas. Estornadas no recorte: {num.format(data.kpis.reversedInvoices)} ({brl0.format(data.kpis.reversedTotal)}).
                </Text>
              )}
              {!(status === "all" && data.kpis.reversedInvoices > 0) && <Box mb="16px" />}

              <Grid templateColumns={isMobile ? "1fr" : "3fr 2fr"} gap="16px" mb="16px">
                <Panel title="Faturamento por competência" subtitle="Valor total por mês de emissão (todos os meses do ano)">
                  <BarChart
                    height={280}
                    xAxis={[
                      {
                        data: data.monthly.map((m) => monthLabel(m.month)),
                        scaleType: "band",
                        tickLabelStyle: { fontSize: 11, fill: crm.textFaint },
                      },
                    ]}
                    yAxis={[{ tickLabelStyle: { fontSize: 11, fill: crm.textFaint }, valueFormatter: (v: number) => brl0.format(v) }]}
                    series={[
                      {
                        data: data.monthly.map((m) => m.total),
                        label: "Faturamento",
                        color: crm.brand,
                        valueFormatter: (v, ctx) =>
                          `${brl.format(v ?? 0)} · ${data.monthly[ctx.dataIndex]?.invoices ?? 0} NF(s)`,
                      },
                    ]}
                    margin={{ left: 90, right: 10, top: 20, bottom: 30 }}
                  />
                </Panel>
                <Panel title="Participação por empresa">
                  {data.byCompany.length === 0 ? (
                    <EmptyState title="Sem notas no recorte." />
                  ) : (
                    <PieChart
                      height={260}
                      series={[
                        {
                          data: data.byCompany.map((c, i) => ({
                            id: c.companyType,
                            value: c.total,
                            label: `${c.company} (${pctFmt(c.share)})`,
                            color: PIE_COLORS[i % PIE_COLORS.length],
                          })),
                          innerRadius: 50,
                          paddingAngle: 2,
                          valueFormatter: (v) => brl.format(v.value),
                        },
                      ]}
                      slotProps={{ legend: { direction: "column", position: { vertical: "bottom", horizontal: "middle" } } }}
                      margin={{ top: 10, bottom: 80, left: 10, right: 10 }}
                    />
                  )}
                </Panel>
              </Grid>

              <Grid templateColumns={isMobile ? "1fr" : "1fr 1fr 1fr"} gap="16px" mb="16px">
                <Panel title="Principais clientes" subtitle="Top 10 por faturamento">
                  {data.topClients.length === 0 ? (
                    <EmptyState title="Sem notas no recorte." />
                  ) : (
                    data.topClients.map((c) => (
                      <RankBar key={c.name} label={c.name} value={c.total} max={maxClient} display={brl0.format(c.total)} />
                    ))
                  )}
                </Panel>
                <Panel title="Processos por faturamento" subtitle="Top 10 processos vinculados às NFs">
                  {data.topProcesses.length === 0 ? (
                    <EmptyState title="Nenhuma NF com processo." />
                  ) : (
                    data.topProcesses.map((c) => (
                      <RankBar key={c.name} label={c.name} value={c.total} max={maxProcess} display={brl0.format(c.total)} />
                    ))
                  )}
                </Panel>
                <Panel title="Composição de tributos">
                  {taxRows.every((t) => t.value === 0) ? (
                    <EmptyState title="Sem tributos no recorte." />
                  ) : (
                    taxRows.map((t) => (
                      <RankBar key={t.label} label={t.label} value={t.value} max={maxTax} display={brl0.format(t.value)} />
                    ))
                  )}
                </Panel>
              </Grid>

              <Panel
                title="Notas fiscais"
                subtitle={table ? `${num.format(table.totalCount)} nota(s)` : undefined}
                action={
                  <Button size="sm" variant="outline" leftIcon={<FaFileDownload />} onClick={exportInvoices} isDisabled={!table || table.totalCount === 0}>
                    Exportar
                  </Button>
                }
              >
                {!table || table.items.length === 0 ? (
                  tableLoading ? (
                    <Flex justify="center" py="24px">
                      <Spinner color={crm.brand} />
                    </Flex>
                  ) : (
                    <EmptyState title="Nenhuma nota fiscal no recorte." />
                  )
                ) : (
                  <Box overflowX="auto" opacity={tableLoading ? 0.6 : 1}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          {columns.map((c) => (
                            <th
                              key={c.key}
                              onClick={() => toggleSort(c.key)}
                              style={{ ...tableHeadStyle, cursor: "pointer", textAlign: c.right ? "right" : "left" }}
                            >
                              {c.label}
                              {sortBy === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {table.items.map((i) => (
                          <tr key={i.id}>
                            <td style={tableCellStyle}>
                              <Text fontSize="sm">{fmtDate(i.issueDate)}</Text>
                              <Text fontSize="xs" color={crm.textMuted}>
                                {i.company}
                              </Text>
                            </td>
                            <td style={tableCellStyle}>
                              {i.number}
                              {i.series ? `/${i.series}` : ""}
                              {i.source === "system" && (
                                <Badge ml="6px" colorScheme="blue" variant="subtle" title="Emitida pelo sistema">
                                  NF-e
                                </Badge>
                              )}
                            </td>
                            <td style={tableCellStyle}>{i.client}</td>
                            <td style={tableCellStyle}>
                              <Text fontSize="sm">{i.processRef || "–"}</Text>
                              {i.declarationNumber && (
                                <Text fontSize="xs" color={crm.textMuted}>
                                  DI/DUIMP {i.declarationNumber}
                                </Text>
                              )}
                            </td>
                            <td style={{ ...tableCellStyle, textAlign: "right" }}>{brl.format(i.products)}</td>
                            <td style={{ ...tableCellStyle, textAlign: "right" }}>{brl.format(i.taxes)}</td>
                            <td style={{ ...tableCellStyle, textAlign: "right", fontWeight: 600 }}>{brl.format(i.total)}</td>
                            <td style={tableCellStyle}>
                              <Badge colorScheme={i.status === "Estornada" ? "red" : "green"}>
                                {i.source === "system" && i.status === "Estornada" ? "Cancelada" : i.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}
                {table && table.totalCount > table.pageSize && (
                  <Flex justify="space-between" align="center" mt="14px" gap="8px" flexWrap="wrap">
                    <Text fontSize="sm" color={crm.textMuted}>
                      Página {page} de {totalPages}
                    </Text>
                    <Flex gap="8px">
                      <Button size="sm" variant="outline" onClick={() => setPage(page - 1)} isDisabled={page <= 1}>
                        Anterior
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPage(page + 1)} isDisabled={page >= totalPages}>
                        Próxima
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </Panel>
            </Box>
          )}
        </Box>
      </Layout>
    </Box>
  );
};

export default Distribuidora;
